import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Activity, RefreshCw, Loader2 } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const ACTION_LABELS = {
  job_create: "created job", job_edit: "edited job", job_publish: "published job",
  job_deadline_change: "changed deadline", job_pay_change: "changed job pay", job_delete: "deleted job",
  submission_request_revision: "requested revision", submission_manager_approve: "manager-approved submission",
  submission_final_approve: "final-approved submission", submission_reopen_review: "reopened review",
  submission_delete: "deleted submission",
  application_hire: "hired applicant", application_reject: "rejected applicant", application_recommend: "recommended applicant",
  payment_mark_paid: "marked payment paid", payment_delete: "deleted payment",
  worker_delete: "deleted worker", manager_promote: "promoted to manager", manager_demote: "demoted to worker",
  permissions_update: "updated manager permissions", flag_owner_attention: "flagged for owner attention",
  rename_announcement: "sent rename announcement",
};
const TARGET_TYPES = ["", "job", "submission", "application", "payment", "worker", "permissions", "settings"];

const inputCls =
  "rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none";

export const AdminActivity = () => {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ actor: "", action: "", target_type: "", date_from: "", date_to: "" });
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    axios.get(`${API}/admin/activity?${params}`, { headers: authHeaders() })
      .then((r) => setItems(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else setItems([]);
      });
  }, [filters, navigate]);

  useEffect(load, [load]);

  const actions = [...new Set((items || []).map((i) => i.action))];

  return (
    <div data-testid="admin-activity">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Activity log</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Every staff action, auditable — who did what, when, and what changed.</p>
        </div>
        <button data-testid="activity-refresh-btn" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div data-testid="activity-filters" className="mt-5 flex flex-wrap gap-2">
        <input data-testid="activity-filter-actor" value={filters.actor} onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))} placeholder="User email…" className={inputCls} />
        <select data-testid="activity-filter-action" value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))} className={inputCls}>
          <option value="">All actions</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="activity-filter-target" value={filters.target_type} onChange={(e) => setFilters((f) => ({ ...f, target_type: e.target.value }))} className={inputCls}>
          {TARGET_TYPES.map((t) => <option key={t} value={t}>{t || "All targets"}</option>)}
        </select>
        <input data-testid="activity-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className={inputCls} />
        <input data-testid="activity-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className={inputCls} />
      </div>

      {!items ? (
        <p data-testid="activity-loading" className="py-16 text-center text-sm text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : items.length === 0 ? (
        <div data-testid="activity-empty" className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
          <Activity className="h-10 w-10 text-[var(--pv-text-2)]" />
          <p className="text-sm font-medium text-[var(--pv-text-4)]">No activity matches these filters.</p>
        </div>
      ) : (
        <div data-testid="activity-table" className="mt-6 overflow-x-auto rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
          <table className="w-full min-w-[760px] text-left text-xs">
            <thead>
              <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                <th className="px-5 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Change</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`activity-row-${it.id}`} className="border-b border-[var(--pv-border)] last:border-0">
                  <td className="whitespace-nowrap px-5 py-3 text-[var(--pv-text-4)]">{new Date(it.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                  <td className="px-4 py-3">
                    <span className="font-bold text-[var(--pv-text)]">{it.actor_name}</span>
                    <span className={`ml-2 rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${it.actor_role === "admin" ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-300" : "border-violet-500/30 bg-violet-500/10 text-violet-300"}`}>{it.actor_role}</span>
                  </td>
                  <td className="px-4 py-3 text-[var(--pv-text-2)]">{ACTION_LABELS[it.action] || it.action.replace(/_/g, " ")}</td>
                  <td className="max-w-[220px] truncate px-4 py-3 text-[var(--pv-text-3)]">{it.target_label || it.target_type || "—"}</td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-[var(--pv-text-4)]">
                    {it.old_value != null && it.new_value != null ? `${it.old_value} → ${it.new_value}` : it.new_value ?? it.old_value ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
